/**
 * SettlementInstructionActionEditor — domain-specific action editor for settlement_instructions context.
 *
 * Layout order (per UX feedback):
 *   1. Two-column account selection (Cargar / Abonar) with cascading dropdowns + copy button
 *   2. Central Bank Trade Code (only when rule conditions target Spot)
 *   3. Document template + output format (filtered to settlement_instructions context)
 *
 * Cascading auto-populate rules:
 *   - Bank selected → auto-fill SWIFT if only one SWIFT for that bank+currency
 *   - SWIFT selected → auto-fill account if only one account for that bank+currency+SWIFT
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { customDocumentService } from '../../services/api/customDocumentService';
import { clientService, type BankAccount } from '../../services/api/clientService';
import type { DocumentTemplateSummary } from '../../types/customDocuments';
import type { ConditionGroup } from '../../types/rulesEngine';
import { getTradeCodeOptions } from '../../constants/tradeCodes';

interface Props {
  action: Record<string, unknown>;
  onChange: (action: Record<string, unknown>) => void;
  conditionGroups?: ConditionGroup[];
}

// ── Helpers ──

/** Unique sorted values for a BankAccount field, optionally pre-filtered. */
function uniqueValues(accounts: BankAccount[], field: keyof BankAccount, filter?: Partial<Record<keyof BankAccount, string>>): string[] {
  let filtered = accounts;
  if (filter) {
    for (const [k, v] of Object.entries(filter)) {
      if (v) filtered = filtered.filter((a) => a[k as keyof BankAccount] === v);
    }
  }
  const set = new Set(filtered.map((a) => String(a[field])).filter(Boolean));
  return Array.from(set).sort();
}

/** Derive whether the rule's conditions explicitly target Spot product type. */
function conditionsTargetSpot(groups?: ConditionGroup[]): boolean {
  if (!groups || groups.length === 0) return true;
  for (const group of groups) {
    for (const cond of group.conditions) {
      if (cond.field === 'ProductType') {
        if (cond.operator === 'equals' && cond.value === 'Spot') return true;
        if (cond.operator === 'in' && Array.isArray(cond.value) && cond.value.includes('Spot')) return true;
        if (cond.operator === 'equals' && cond.value !== 'Spot') return false;
        if (cond.operator === 'not_equals' && cond.value === 'Spot') return false;
      }
    }
  }
  return true;
}

/** Extract counterparty names targeted by the rule conditions (empty = all/unknown). */
function getCounterpartyNamesFromConditions(groups?: ConditionGroup[]): string[] {
  if (!groups || groups.length === 0) return [];
  const names: string[] = [];
  for (const group of groups) {
    for (const cond of group.conditions) {
      if (cond.field === 'CounterpartyName') {
        if (cond.operator === 'equals' && typeof cond.value === 'string') {
          names.push(cond.value);
        } else if (cond.operator === 'in' && Array.isArray(cond.value)) {
          names.push(...cond.value);
        }
      }
    }
  }
  return [...new Set(names)];
}

const SettlementInstructionActionEditor: React.FC<Props> = ({ action, onChange, conditionGroups }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [templates, setTemplates] = useState<DocumentTemplateSummary[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // Parsed values from the generic action object
  const templateId = (action.templateId as string) || '';
  const outputFormat = (action.outputFormat as string) || 'DOCX';
  const centralBankTradeCode = (action.centralBankTradeCode as string) || '';

  // Cascading dropdown selections (Cargar)
  const cargarCurrency = (action.cargarCurrency as string) || '';
  const cargarBankName = (action.cargarBankName as string) || '';
  const cargarSwiftCode = (action.cargarSwiftCode as string) || '';
  const cargarAccountId = (action.cargarAccountId as string) || '';

  // Cascading dropdown selections (Abonar)
  const abonarCurrency = (action.abonarCurrency as string) || '';
  const abonarBankName = (action.abonarBankName as string) || '';
  const abonarSwiftCode = (action.abonarSwiftCode as string) || '';
  const abonarAccountId = (action.abonarAccountId as string) || '';

  // Cargar account type toggle (nostro = our account, vostro = counterparty's account)
  const cargarAccountType = (action.cargarAccountType as string) || 'nostro';

  // Counterparty names extracted from rule conditions (empty = catch-all rule)
  const ruleCounterpartyNames = useMemo(() => getCounterpartyNamesFromConditions(conditionGroups), [conditionGroups]);
  const hasCounterpartyCondition = ruleCounterpartyNames.length > 0;

  // Timing checkboxes
  const timing = (action.timing as string[]) || ['onExpiryConfirmation'];

  const showTradeCode = useMemo(() => conditionsTargetSpot(conditionGroups), [conditionGroups]);

  // Filtered account lists: cargar respects the nostro/vostro toggle, abonar is always nostro
  const cargarAccounts = useMemo(() => {
    if (cargarAccountType === 'vostro') {
      return bankAccounts.filter(a =>
        a.accountType === 'vostro' &&
        (!hasCounterpartyCondition || ruleCounterpartyNames.includes(a.counterpartyName || ''))
      );
    }
    return bankAccounts.filter(a => !a.accountType || a.accountType === 'nostro');
  }, [bankAccounts, cargarAccountType, hasCounterpartyCondition, ruleCounterpartyNames]);

  const abonarAccounts = useMemo(() => {
    return bankAccounts.filter(a => !a.accountType || a.accountType === 'nostro');
  }, [bankAccounts]);

  // Load templates (filtered to settlement_instructions) and bank accounts
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [tpls, acctResp] = await Promise.all([
          customDocumentService.listTemplates(clientId, 'settlement_instructions'),
          clientService.getBankAccounts(clientId),
        ]);
        if (cancelled) return;
        setTemplates(tpls.filter((tp) => tp.isActive));
        setBankAccounts((acctResp.data || []).filter((a: BankAccount) => a.active !== false));
      } catch (err) {
        console.error('Failed to load action editor data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [clientId]);

  const update = useCallback((patch: Record<string, unknown>) => {
    onChange({ ...action, ...patch });
  }, [action, onChange]);

  // ── Build account name summary from a fully-selected account ──
  const buildAccountName = useCallback((accountId: string): string => {
    const acct = bankAccounts.find((a) => a.id === accountId);
    return acct ? `${acct.bankName} – ${acct.accountNumber}` : '';
  }, [bankAccounts]);

  // ── Cascading account logic with auto-populate ──

  const handleCascade = useCallback((side: 'cargar' | 'abonar', level: 'currency' | 'bankName' | 'swiftCode' | 'accountId', value: string) => {
    const p = side; // prefix
    const sideAccounts = side === 'cargar' ? cargarAccounts : abonarAccounts;

    if (level === 'currency') {
      const patch: Record<string, unknown> = {
        [`${p}Currency`]: value,
        [`${p}BankName`]: '',
        [`${p}SwiftCode`]: '',
        [`${p}AccountId`]: '',
        [`${p}AccountName`]: '',
      };

      // Auto-populate bank if only one bank for this currency
      if (value) {
        const banks = uniqueValues(sideAccounts, 'bankName', { accountCurrency: value });
        if (banks.length === 1) {
          patch[`${p}BankName`] = banks[0];

          // Continue cascade: auto-populate SWIFT
          const swifts = uniqueValues(sideAccounts, 'swiftCode', { accountCurrency: value, bankName: banks[0] });
          if (swifts.length === 1) {
            patch[`${p}SwiftCode`] = swifts[0];

            // Continue cascade: auto-populate account
            const accts = sideAccounts.filter((a) => a.accountCurrency === value && a.bankName === banks[0] && a.swiftCode === swifts[0]);
            if (accts.length === 1) {
              patch[`${p}AccountId`] = accts[0].id!;
              patch[`${p}AccountName`] = `${accts[0].bankName} – ${accts[0].accountNumber}`;
            }
          }
        }
      }

      onChange({ ...action, ...patch });

    } else if (level === 'bankName') {
      const currencyVal = side === 'cargar' ? cargarCurrency : abonarCurrency;
      const patch: Record<string, unknown> = {
        [`${p}BankName`]: value,
        [`${p}SwiftCode`]: '',
        [`${p}AccountId`]: '',
        [`${p}AccountName`]: '',
      };

      // Auto-populate SWIFT if only one for this bank+currency
      if (value) {
        const swifts = uniqueValues(sideAccounts, 'swiftCode', { accountCurrency: currencyVal, bankName: value });
        if (swifts.length === 1) {
          patch[`${p}SwiftCode`] = swifts[0];

          // Continue cascade: auto-populate account
          const accts = sideAccounts.filter((a) => a.accountCurrency === currencyVal && a.bankName === value && a.swiftCode === swifts[0]);
          if (accts.length === 1) {
            patch[`${p}AccountId`] = accts[0].id!;
            patch[`${p}AccountName`] = `${accts[0].bankName} – ${accts[0].accountNumber}`;
          }
        }
      }

      onChange({ ...action, ...patch });

    } else if (level === 'swiftCode') {
      const currencyVal = side === 'cargar' ? cargarCurrency : abonarCurrency;
      const bankVal = side === 'cargar' ? cargarBankName : abonarBankName;
      const patch: Record<string, unknown> = {
        [`${p}SwiftCode`]: value,
        [`${p}AccountId`]: '',
        [`${p}AccountName`]: '',
      };

      // Auto-populate account if only one for this bank+currency+SWIFT
      if (value) {
        const accts = sideAccounts.filter((a) => a.accountCurrency === currencyVal && a.bankName === bankVal && a.swiftCode === value);
        if (accts.length === 1) {
          patch[`${p}AccountId`] = accts[0].id!;
          patch[`${p}AccountName`] = `${accts[0].bankName} – ${accts[0].accountNumber}`;
        }
      }

      onChange({ ...action, ...patch });

    } else if (level === 'accountId') {
      onChange({
        ...action,
        [`${p}AccountId`]: value,
        [`${p}AccountName`]: buildAccountName(value),
      });
    }
  }, [action, onChange, cargarAccounts, abonarAccounts, buildAccountName, cargarCurrency, cargarBankName, abonarCurrency, abonarBankName]);

  // ── Copy account details from one side to the other ──
  const copyAccount = useCallback((from: 'cargar' | 'abonar') => {
    const to = from === 'cargar' ? 'abonar' : 'cargar';
    onChange({
      ...action,
      [`${to}Currency`]: action[`${from}Currency`] || '',
      [`${to}BankName`]: action[`${from}BankName`] || '',
      [`${to}SwiftCode`]: action[`${from}SwiftCode`] || '',
      [`${to}AccountId`]: action[`${from}AccountId`] || '',
      [`${to}AccountName`]: action[`${from}AccountName`] || '',
    });
  }, [action, onChange]);

  // ── Toggle cargar between nostro/vostro (resets cascade selections) ──
  const handleCargarTypeToggle = useCallback((type: 'nostro' | 'vostro') => {
    onChange({
      ...action,
      cargarAccountType: type,
      cargarCurrency: '',
      cargarBankName: '',
      cargarSwiftCode: '',
      cargarAccountId: '',
      cargarAccountName: '',
    });
  }, [action, onChange]);

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    const tpl = templates.find((tp) => tp.templateId === id);
    update({ templateId: id, templateName: tpl?.name || '' });
  };

  // ── Render one side of the account cascade ──

  const renderAccountColumn = (
    side: 'cargar' | 'abonar',
    label: string,
    currency: string,
    selectedBankName: string,
    swiftCode: string,
    accountId: string,
    filteredAccounts: BankAccount[],
  ) => {
    const currencies = uniqueValues(filteredAccounts, 'accountCurrency');
    const bankNames = currency ? uniqueValues(filteredAccounts, 'bankName', { accountCurrency: currency }) : [];
    const swiftCodes = selectedBankName ? uniqueValues(filteredAccounts, 'swiftCode', { accountCurrency: currency, bankName: selectedBankName }) : [];
    const accounts = swiftCode
      ? filteredAccounts.filter((a) => a.accountCurrency === currency && a.bankName === selectedBankName && a.swiftCode === swiftCode)
      : [];

    return (
      <div className="account-column">
        <div className="account-column-header">
          <span className="account-column-title">{label}</span>
          {side === 'cargar' && (
            <div className="account-type-toggle">
              <button
                type="button"
                className={`toggle-btn${cargarAccountType === 'nostro' ? ' active' : ''}`}
                onClick={() => handleCargarTypeToggle('nostro')}
              >
                {t('admin.settlementInstructions.nostro', 'Nuestra')}
              </button>
              <button
                type="button"
                className={`toggle-btn${cargarAccountType === 'vostro' ? ' active' : ''}`}
                onClick={() => handleCargarTypeToggle('vostro')}
                disabled={!hasCounterpartyCondition}
                title={!hasCounterpartyCondition ? t('admin.settlementInstructions.vostroDisabledHint', 'Agregue una condición de Contraparte para habilitar cuentas vostro') : ''}
              >
                {t('admin.settlementInstructions.vostro', 'Contraparte')}
              </button>
            </div>
          )}
        </div>
        {side === 'cargar' && cargarAccountType === 'vostro' && hasCounterpartyCondition && (
          <div className="account-type-hint">
            {ruleCounterpartyNames.join(', ')}
          </div>
        )}

        <div className="cascade-field">
          <label>{t('admin.settlementInstructions.currency', 'Moneda')}</label>
          <select value={currency} onChange={(e) => handleCascade(side, 'currency', e.target.value)}>
            <option value="">--</option>
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="cascade-field">
          <label>{t('admin.settlementInstructions.bankNameLabel', 'Nombre del Banco')}</label>
          <select value={selectedBankName} onChange={(e) => handleCascade(side, 'bankName', e.target.value)} disabled={!currency}>
            <option value="">--</option>
            {bankNames.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        <div className="cascade-field">
          <label>{t('admin.settlementInstructions.swiftCodeLabel', 'Código SWIFT')}</label>
          <select value={swiftCode} onChange={(e) => handleCascade(side, 'swiftCode', e.target.value)} disabled={!selectedBankName}>
            <option value="">--</option>
            {swiftCodes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="cascade-field">
          <label>{t('admin.settlementInstructions.accountNumberLabel', 'Número de Cuenta')}</label>
          <select value={accountId} onChange={(e) => handleCascade(side, 'accountId', e.target.value)} disabled={!swiftCode}>
            <option value="">--</option>
            {accounts.map((a) => <option key={a.id} value={a.id!}>{a.accountNumber}</option>)}
          </select>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="action-editor-loading">
        <div className="action-spinner" />
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  const toggleTiming = (key: string) => {
    const current = (action.timing as string[]) || ['onExpiryConfirmation'];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    update({ timing: next });
  };

  return (
    <div className="settlement-instruction-action-editor">

      {/* ── 1. Account Selection (two-column) ── */}
      <div className="action-section-title">
        {t('admin.settlementInstructions.accountSettlement', 'Liquidar en las siguientes cuentas')}
      </div>

      {bankAccounts.length === 0 ? (
        <p className="text-muted">{t('admin.settlementInstructions.noAccounts', 'No bank accounts configured.')}</p>
      ) : (
        <div className="account-columns">
          {renderAccountColumn(
            'cargar',
            t('admin.settlementInstructions.pagamosAccount', 'Pagamos'),
            cargarCurrency, cargarBankName, cargarSwiftCode, cargarAccountId,
            cargarAccounts,
          )}
          <div className="account-copy-arrows">
            <button
              type="button"
              className="copy-arrow-btn"
              onClick={() => copyAccount('cargar')}
              disabled={!cargarCurrency}
              title={t('admin.settlementInstructions.copyToRecibimos', 'Copiar a Recibimos')}
            >
              &rarr;
            </button>
            <button
              type="button"
              className="copy-arrow-btn"
              onClick={() => copyAccount('abonar')}
              disabled={!abonarCurrency}
              title={t('admin.settlementInstructions.copyToPagamos', 'Copiar a Pagamos')}
            >
              &larr;
            </button>
          </div>
          {renderAccountColumn(
            'abonar',
            t('admin.settlementInstructions.recibimosAccount', 'Recibimos'),
            abonarCurrency, abonarBankName, abonarSwiftCode, abonarAccountId,
            abonarAccounts,
          )}
        </div>
      )}

      {/* ── 2. Central Bank Trade Code (Spot only) ── */}
      {showTradeCode && (
        <div className="action-field">
          <label>{t('admin.settlementInstructions.centralBankTradeCode', 'Código de Comercio (Banco Central)')} <span className="optional">({t('common.optional', 'optional')})</span></label>
          <select
            value={centralBankTradeCode}
            onChange={(e) => update({ centralBankTradeCode: e.target.value })}
          >
            <option value="">{t('admin.settlementInstructions.selectTradeCode', '-- Seleccionar código --')}</option>
            {getTradeCodeOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── 4. Template + Output Format ── */}
      <div className="action-section-title" style={{ marginTop: '1rem' }}>
        {t('admin.settlementInstructions.templateSection', 'Crear la siguiente carta de instrucción')}
      </div>

      <div className="action-field">
        <label>{t('admin.settlementInstructions.template', 'Plantilla')} <span className="optional">({t('common.optional', 'optional')})</span></label>
        {templates.length === 0 ? (
          <p className="text-muted">
            {t('admin.settlementInstructions.noTemplates', 'No active settlement instruction templates found. Create one in Custom Documents and set its context to "Settlement Instructions".')}
          </p>
        ) : (
          <select value={templateId} onChange={handleTemplateChange}>
            <option value="">{t('admin.settlementInstructions.selectTemplate', '-- Seleccionar plantilla --')}</option>
            {templates.map((tpl) => (
              <option key={tpl.templateId} value={tpl.templateId}>{tpl.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className={`action-field${!templateId ? ' disabled-section' : ''}`}>
        <label>{t('admin.settlementInstructions.outputFormat', 'Formato')}</label>
        <div className="radio-group">
          <label className="radio-label">
            <input type="radio" name="outputFormat" value="DOCX" checked={outputFormat === 'DOCX'} onChange={() => update({ outputFormat: 'DOCX' })} disabled={!templateId} />
            DOCX
          </label>
          <label className="radio-label">
            <input type="radio" name="outputFormat" value="PDF" checked={outputFormat === 'PDF'} onChange={() => update({ outputFormat: 'PDF' })} disabled={!templateId} />
            PDF
          </label>
        </div>
      </div>

      {/* ── 4. Timing ── */}
      <div className="action-section-title" style={{ marginTop: '1rem' }}>
        {t('admin.settlementInstructions.timingSection', 'Aplicar regla en')}
      </div>
      <div className="timing-checkboxes">
        <label className={`timing-checkbox-label${timing.includes('onTrade') ? ' active-on-trade' : ''}`}>
          <input type="checkbox" checked={timing.includes('onTrade')} onChange={() => toggleTiming('onTrade')} />
          {t('admin.settlementInstructions.timingOnTrade', 'Al ingresar operación')}
        </label>
        <label className="timing-checkbox-label">
          <input type="checkbox" checked={timing.includes('onConfirmation')} onChange={() => toggleTiming('onConfirmation')} />
          {t('admin.settlementInstructions.timingOnConfirmation', 'Al confirmar operación')}
        </label>
        <label className="timing-checkbox-label">
          <input type="checkbox" checked={timing.includes('onExpiryConfirmation')} onChange={() => toggleTiming('onExpiryConfirmation')} />
          {t('admin.settlementInstructions.timingOnExpiry', 'Al vencimiento / unwind')}
        </label>
      </div>
      {timing.includes('onTrade') && (
        <p className="timing-hint">{t('admin.settlementInstructions.onTradeHint')}</p>
      )}
    </div>
  );
};

export default SettlementInstructionActionEditor;
